import { useAppSelector } from "app/hooks";
import { Button } from "components/ui/Button";
import { LightAction } from "components/ui/LightAction";
import { AddIcon, PenIcon, UmbrellaCircleIcon } from "components/icons/Icons";
import { Sizes } from "components/ui/Loader";
import { NsKey } from "enums/NsKey";
import { SubscriptionProcessContext, useSubscriptionProcess } from "features/subscription-process/contexts/SubscriptionProcessContext";
import { usePanel } from "hooks/usePanel";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { selectGlobalGuarantees, selectGlobalGuaranteesReferential } from "../../slices/pricing/selectors/guaranteeFilterSelector";
import GuaranteeFilter from "./GuaranteeFilter";

export const GuaranteesCard: React.FC = () => {
  const { t } = useTranslation(NsKey.Pricing)
  const { hasOptionalGuarantees } = useAppSelector(selectGlobalGuaranteesReferential);
  const contextValue = useSubscriptionProcess()
  const { openPanel } = usePanel()
  const globalGuarantees = useAppSelector(selectGlobalGuarantees);

  const onSelectGuarantee = useCallback(
    (isOptional: boolean) => {
      openPanel({
        title: "Garanties",
        size: Sizes["4xl"],
      },
        <SubscriptionProcessContext.Provider value={contextValue}>
          <GuaranteeFilter isOptional={isOptional} />
        </SubscriptionProcessContext.Provider>
      )
    }, [openPanel, contextValue])

  return (
    <div className="flex border rounded-xl max-h-32 grow overflow-hidden">
      <div className={`flex grow w-full p-4 border-r`}>
        <div className="mr-4">
          <UmbrellaCircleIcon className="w-9 h-9 text-secondary" />
        </div>
        <div className="flex flex-col w-full gap-2">
          <p className="text-sm font-light">{t("PRICING.GUARANTEES.MANDATORY")}</p>
          <div className={`flex gap-1 flex-wrap overflow-y-auto`}>
            {
              globalGuarantees.mandatory.map((guarantee) => (
                <p key={guarantee.id} className="text-xs text-primary border border-primary py-0 px-1.5 rounded-full">
                  {guarantee.shortName ? guarantee.shortName : guarantee.name}
                </p>
              ))
            }
          </div>
        </div>
        <Button color="slate" round className="self-baseline" onClick={() => onSelectGuarantee(false)}>
          <PenIcon className="text-black" />
        </Button>
      </div>
      {
        hasOptionalGuarantees &&
        <div className={`flex w-full grow p-4`}>
          <div className="mr-4">
            <UmbrellaCircleIcon className="w-9 h-9 text-secondary" />
          </div>
          <div className="flex flex-col w-full gap-2">
            <p className="text-sm font-light">{t("PRICING.GUARANTEES.OPTIONAL")}</p>
            {
              globalGuarantees.optional.length === 0 &&
              <div className="w-full h-full flex items-center justify-center">
                <LightAction icon={AddIcon} label="Ajouter" onClick={() => onSelectGuarantee(true)} />
              </div>
            }
            {
              globalGuarantees.optional.length > 0 &&
              <div className="flex gap-1 overflow-y-auto flex-wrap">
                {
                  globalGuarantees.optional.map((guarantee) => (
                    <p key={guarantee.id} className="text-xs text-primary border border-primary py-0 px-1.5 rounded-full">
                      {guarantee.shortName ? guarantee.shortName : guarantee.name}
                    </p>
                  ))
                }
              </div>
            }
          </div>
          <Button color="slate" round className={`self-baseline ${globalGuarantees.optional.length > 0 ? 'visible' : 'invisible'}`}
            onClick={() => onSelectGuarantee(true)}>
            <PenIcon className="text-black" />
          </Button>
        </div>
      }
    </div>
  )
}
